<script setup>
import { ref, computed, h, getCurrentInstance } from "vue";
import { useI18n } from "vue-i18n";
import { NewspaperIcon, RssIcon, LinkIcon } from "@heroicons/vue/24/outline";
import { FileListIcon, NodeTreeIcon, MagicIcon, YouTubeIcon } from "@Icons/index";
import {
    BookOpenIcon,
    BotIcon,
    BrainCircuitIcon,
    ChartNoAxesColumnIcon,
    CircleDollarSignIcon,
    FilesIcon,
    ImageIcon,
    InfoIcon,
    ListIcon,
    MapPinIcon,
    NetworkIcon,
    PencilLineIcon,
    PinIcon,
    SquarePenIcon,
    TagIcon,
    UserIcon,
} from "lucide-vue-next";
import ToolModal from "@Components/Modal/ToolModal.vue";
import { PrimaryButton } from "@Components/Button";
import { FormDropdown, FormDropdownItem, FormDropdownTrigger } from "@Components/Form";
import AppLink from "@Components/AppLink.vue";
import { useRouter } from "vue-router";

const { t: __ } = useI18n();

const sourcesModal = ref();
const scrapingPostTypesModal = ref();
const keywordsPostTypesModal = ref();
const youtubePostTypesModal = ref();
const keywordsStylesModal = ref();
const saasUrl = getCurrentInstance().appContext.config.globalProperties.$app.saas_url;
const saasDomainUrl = getCurrentInstance().appContext.config.globalProperties.$app.saas_domain_url;
const domain = getCurrentInstance().appContext.config.globalProperties.$app.domain;

const projectType = ref();
const keywordsPostType = ref("post");

const router = useRouter();

const tools = [
    {
        id: "single-mode",
        icon: PencilLineIcon,
        name: __("Générer un contenu IA"),
        href: { name: "single-mode.create" },
        description: __("Générez un article ou une page unique avec l'IA."),
    },
    {
        id: "autopilot",
        icon: BrainCircuitIcon,
        name: __("Génération Autopilot"),
        description: __("Créez un projet en autopilot pour publier automatiquement du contenu sur la durée."),
        onClick: () => {
            sourcesModal.value.show();
        },
    },
    {
        id: "image-generator",
        icon: ImageIcon,
        name: __("Générer une image IA"),
        href: { name: "image-generator" },
        description: __("Générez une image unique avec l'IA."),
    },
    {
        id: "personas",
        icon: UserIcon,
        name: __("Générer un profil IA"),
        href: { name: "personas.create" },
        description: __("Créez un profil IA pour générer du contenu personnalisé."),
    },
    // {
    //     id: "assistant",
    //     icon: BotIcon,
    //     name: __("Assistant IA"),
    //     href: { name: "one-click" },
    //     beta: true,
    //     description: __("Utilisez notre assistant IA pour générer du contenu rapidement et efficacement."),
    // },
];

const sourcesTools = computed(() => [
    {
        icon: FileListIcon,
        name: __("Mots clés"),
        onClick: () => {
            projectType.value = "bulk";
            keywordsPostTypesModal.value.show();
        },
    },
    {
        icon: RssIcon,
        name: __("Flux RSS"),
        onClick: () => {
            projectType.value = "rss";
            scrapingPostTypesModal.value.show();
        },
    },
    {
        icon: NewspaperIcon,
        name: __("GNews"),
        onClick: () => {
            projectType.value = "news";
            scrapingPostTypesModal.value.show();
        },
    },
    {
        icon: NodeTreeIcon,
        name: __("Sitemap"),
        onClick: () => {
            projectType.value = "sitemap";
            scrapingPostTypesModal.value.show();
        },
    },
    {
        icon: LinkIcon,
        name: __("URLs"),
        onClick: () => {
            projectType.value = "url";
            scrapingPostTypesModal.value.show();
        },
    },
    {
        icon: YouTubeIcon,
        name: __("YouTube"),
        onClick: () => {
            projectType.value = "youtube";
            youtubePostTypesModal.value.show();
        },
    },
]);

const keywordsPostTypesTools = computed(() => [
    {
        icon: PinIcon,
        name: __("Articles"),
        onClick: () => {
            keywordsPostType.value = "post";
            keywordsStylesModal.value.show();
        },
    },
    {
        icon: FilesIcon,
        name: __("Pages"),
        onClick: () => {
            keywordsPostType.value = "page";
            keywordsStylesModal.value.show();
        },
    },
]);

const keywordsStylesTools = computed(() => {
    let tools = [];
    if (keywordsPostType.value === "page") {
        tools.push({
            icon: NetworkIcon,
            name: __("Siloing"),
            href: { name: "projects.create", params: { type: "bulk", postType: keywordsPostType.value, style: "siloing" }, force: true },
        });
    }

    tools = [
        ...tools,
        {
            icon: InfoIcon,
            name: __("Informatif"),
            href: { name: "projects.create", params: { type: "bulk", postType: keywordsPostType.value, style: "informative" }, force: true },
        },
        {
            icon: CircleDollarSignIcon,
            name: __("Commercial"),
            href: { name: "projects.create", params: { type: "bulk", postType: keywordsPostType.value, style: "commercial" }, force: true },
        },
        {
            icon: MapPinIcon,
            name: __("Local"),
            href: { name: "projects.create", params: { type: "bulk", postType: keywordsPostType.value, style: "local" }, force: true },
        },
        {
            icon: TagIcon,
            name: __("Marques"),
            href: { name: "projects.create", params: { type: "bulk", postType: keywordsPostType.value, style: "brands" }, force: true },
        },
        {
            icon: ChartNoAxesColumnIcon,
            name: __("Comparatif"),
            href: { name: "projects.create", params: { type: "bulk", postType: keywordsPostType.value, style: "comparative" }, force: true },
        },
        {
            icon: BookOpenIcon,
            name: __("Guide"),
            href: { name: "projects.create", params: { type: "bulk", postType: keywordsPostType.value, style: "guide" }, force: true },
        },
    ];

    return tools;
});

const HaloScanIcon = h("div", [
    h(
        "span",
        {
            class: "inline-flex items-center pointer-events-none text-gray-900 dark:text-white text-[25px]",
        },
        "〉"
    ),
]);

const keywordsStyles2Tools = computed(() => [
    {
        icon: SquarePenIcon,
        name: __("Ajouter mes titres manuellement"),
        href: { name: "projects.create", params: { type: "bulk", postType: keywordsPostType.value }, force: true },
    },
    {
        icon: ListIcon,
        name: __("Ajouter une liste de mots clés"),
        href: { name: "projects.create", params: { type: "bulk", postType: keywordsPostType.value, style: "keywords" }, force: true },
    },
    {
        icon: HaloScanIcon,
        name: __("Haloscan"),
        href: { name: "projects.create", params: { type: "bulk", postType: keywordsPostType.value, style: "haloscan" }, force: true },
    },
]);

const youtubePostTypesTools = computed(() => [
    {
        icon: PinIcon,
        name: __("Articles"),
        href: { name: "projects.create", params: { type: "youtube", postType: "post" }, force: true },
    },
    {
        icon: FilesIcon,
        name: __("Pages"),
        href: { name: "projects.create", params: { type: "youtube", postType: "page" }, force: true },
    },
]);

const scrapingPostTypesTools = computed(() => [
    {
        icon: PinIcon,
        name: __("Articles"),
        href: { name: "projects.create", params: { type: projectType.value, postType: "post" }, force: true },
    },
    {
        icon: FilesIcon,
        name: __("Pages"),
        href: { name: "projects.create", params: { type: projectType.value, postType: "page" }, force: true },
    },
]);

const getToolComponent = (tool) => {
    if (tool.href) {
        return AppLink;
    }

    return "button";
};

const getToolAttributes = (tool) => {
    let attrs = {};

    if (tool.href) {
        attrs.to = tool.href;
        attrs.target = tool.target;
    }

    return attrs;
};

const getToolEvents = (tool) => {
    let events = {};

    if (tool.href) {
    } else if (tool.onClick) {
        events.click = () => {
            tool.onClick();
        };
    }

    return events;
};

const getToolTags = (tool) => {
    let tags = [];

    if (tool.beta) {
        tags.push({ label: __("Beta"), color: "black" });
    }

    if (tool.new) {
        tags.push({ label: __("Nouveau"), color: "black" });
    }

    return tags;
};

const open = (tool) => {
    let t = tools.find((t) => t.id === tool);
    if (t) {
        if (t.href) {
            router.push(t.href);
        } else if (t.onClick) {
            t.onClick();
        }
    }
};

defineExpose({
    open,
});
</script>

<template>
    <div>
        <!-- <FormDropdown :z="1" position="right" class="w-64">
            <template #trigger>
                <FormDropdownTrigger class="w-full justify-between" :as="PrimaryButton" :z="1" :icon="MagicIcon">
                    <div class="-ml-4">{{ __("Générer du contenu") }}</div>
                    <div></div>
                </FormDropdownTrigger>
            </template>
            <FormDropdownItem
                v-for="tool in tools"
                :as="getToolComponent(tool)"
                v-bind="getToolAttributes(tool)"
                v-on="getToolEvents(tool)"
                :tags="getToolTags(tool)"
                :key="tool.name"
                :label="tool.name"
                :icon="tool.icon"
                :z="1" />
        </FormDropdown> -->

        <ToolModal ref="sourcesModal" :tools="sourcesTools" :title="__('Créer du contenu automatiquement en Autopilot à partir de')" />
        <ToolModal ref="scrapingPostTypesModal" :tools="scrapingPostTypesTools" :title="__('Sélectionnez le type de publication')" />
        <ToolModal ref="keywordsPostTypesModal" :tools="keywordsPostTypesTools" :title="__('Sélectionnez le type de publication')" />
        <ToolModal ref="youtubePostTypesModal" :tools="youtubePostTypesTools" :title="__('Sélectionnez le type de publication')" />
        <ToolModal
            ref="keywordsStylesModal"
            :tools="keywordsStylesTools"
            :custom-tools="keywordsStyles2Tools"
            :title="__('Sélectionnez le style de titre que vous souhaitez générer')" />
    </div>
</template>
